/**
 * Work portfolio media conventions.
 *
 * Videos used in cards, carousels, and LPs should stay in git as compressed
 * previews (~1–3 MB). Full-resolution sources belong on Contentful
 * (`videos.ctfassets.net`) or another CDN — set `fullUrl` on the work item.
 *
 * @see public/README.md — Work video previews
 */

export type WorkMedia = {
  type: "image" | "video";
  /** Repo-local path (or absolute URL). For video, prefer a `-preview.mp4` under ~3 MB. */
  url: string;
  /** Full-quality CDN URL when `url` is a compressed preview only. */
  fullUrl?: string;
};

/** Additional keyed media beyond `featuredMedia` / `screenshot` — exposed on the Work API. */
export type WorkMediaAsset = {
  key: string;
  label?: string;
  media: WorkMedia;
};

/** Target upper bound for repo-hosted work video previews. */
export const WORK_VIDEO_PREVIEW_MAX_MB = 3;

export const WORK_VIDEO_PREVIEW_SUFFIX = "-preview";

/** Local preview path for a work video slug, e.g. `fca` → `/assets/work/fca-preview.mp4`. */
export function workVideoPreviewPath(slug: string): string {
  return `/assets/work/${slug}${WORK_VIDEO_PREVIEW_SUFFIX}.mp4`;
}

export function isAbsoluteMediaUrl(url: string): boolean {
  return url.startsWith("http://") || url.startsWith("https://");
}

/** Site UI and LPs always use the preview; API consumers may request full when set. */
export function resolveWorkMediaUrl(
  media: WorkMedia,
  variant: "preview" | "full" = "preview",
): string {
  if (variant === "full" && media.fullUrl) {
    return media.fullUrl;
  }
  return media.url;
}

export function workMediaHasRemoteFull(media: WorkMedia): boolean {
  return media.type === "video" && Boolean(media.fullUrl);
}

type WorkDeckMediaSource = {
  deckMediaKey?: string;
  mediaAssets?: WorkMediaAsset[];
};

/** Deck hero from `deckMediaKey` → `mediaAssets` lookup. Site UI keeps `featuredMedia`. */
export function resolveWorkDeckMedia(
  item: WorkDeckMediaSource,
): WorkMedia | undefined {
  if (!item.deckMediaKey) {
    return undefined;
  }

  return item.mediaAssets?.find((asset) => asset.key === item.deckMediaKey)
    ?.media;
}

export function resolveWorkDeckMediaAsset(
  item: WorkDeckMediaSource,
): WorkMediaAsset | undefined {
  if (!item.deckMediaKey) {
    return undefined;
  }

  return item.mediaAssets?.find((asset) => asset.key === item.deckMediaKey);
}
